import moment from "moment";

/** DB `field_key` values that must never appear on corporate self-enrol forms (workflow / admin only). */
export const CORPORATE_ENROLL_APPLICANT_FIELD_BLOCKLIST = new Set([
    "registration_stage",
    "rejection_reason",
    "rejection_history",
]);

/**
 * Explicit signature fields (TMS-style): `field_type === 'signature'` or known URL keys.
 * Other keys containing "signature" stay hidden unless migrated to this shape.
 */
export function isSignaturePadField(field) {
    if (!field || typeof field.field_key !== "string") return false;
    if (field.field_type === "signature") return true;
    if (field.field_key === "signature") return true;
    if (field.field_key === "legal.declaration_signature_url") return true;
    return false;
}

/** Extra keys saved with signature (date / timestamp), aligned with TMS draft forms. */
export function collectSignatureCompanionFieldKeys(templateFieldRecords) {
    const keys = new Set();
    for (const c of templateFieldRecords) {
        if (!shouldShowOnCorporateEnrollForm(c)) continue;
        if (!isSignaturePadField(c)) continue;
        if (c.field_key === "signature") keys.add("date_signed");
        else if (c.field_key.endsWith("_url")) keys.add(c.field_key.replace("_url", "_timestamp"));
    }
    return [...keys];
}

/**
 * Whether this `additional_registration_fields` row may be shown to applicants on corporate enrol.
 * @param {{ field_key?: string, is_hidden?: boolean | null }} field
 */
export function shouldShowOnCorporateEnrollForm(field) {
    if (!field || typeof field.field_key !== "string" || !field.field_key) return false;
    if (field.is_hidden === true) return false;
    if (CORPORATE_ENROLL_APPLICANT_FIELD_BLOCKLIST.has(field.field_key)) return false;
    return true;
}

/** @typedef {{ field_key: string, field_label: string, field_type: string, is_required: boolean, field_options: unknown }} FormColumn */
/** @typedef {{ id: string, participant_data: Record<string, unknown> }} ParticipantDraft */

export function formatPortalDate(value) {
    if (!value) return null;
    const d = moment(value);
    if (!d.isValid()) return value;
    return d.format("D MMM YYYY");
}

export function runDateLine(start, end) {
    const a = formatPortalDate(start);
    const b = formatPortalDate(end);
    if (a && b && a !== b) return `${a} – ${b}`;
    if (a) return a;
    if (b) return b;
    return "Dates to be confirmed";
}

export function groupPublicRunsByCourseType(runs) {
    const m = new Map();
    for (const r of runs) {
        const key = r.course_type_name ?? r.course_type_code ?? "Programs";
        const list = m.get(key) ?? [];
        list.push(r);
        m.set(key, list);
    }
    return m;
}

export function parseOrgBranding(raw) {
    if (!raw || typeof raw !== "object") return { name: null, logo_url: null };
    return {
        name: typeof raw.name === "string" ? raw.name : null,
        logo_url: typeof raw.logo_url === "string" ? raw.logo_url : null,
    };
}

/** UEN from `corporate_enrollment_portal` / RPC: trim + uppercase for display and sponsor field. */
export function normalizeCorporatePortalUen(raw) {
    if (typeof raw !== "string") return "";
    return raw.trim().toUpperCase();
}

/**
 * Parses `employer_contact_defaults` from `get_corporate_portal_run_participants` (jsonb object).
 * Keys: registration_type, sponsor_uen, employer_contact_name, employer_contact_email, employer_contact_number.
 * @param {unknown} raw
 * @returns {Record<string, string>}
 */
export function parseEmployerContactDefaults(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const o = /** @type {Record<string, string>} */ ({});
    const setStr = (k) => {
        const v = raw[k];
        if (v === null || v === undefined) return;
        const s = typeof v === "string" ? v.trim() : String(v).trim();
        if (s) o[k] = s;
    };
    setStr("registration_type");
    setStr("employer_contact_name");
    setStr("employer_contact_email");
    setStr("employer_contact_number");
    if (raw.sponsor_uen !== null && raw.sponsor_uen !== undefined && String(raw.sponsor_uen).trim()) {
        o.sponsor_uen = normalizeCorporatePortalUen(String(raw.sponsor_uen));
    }
    return o;
}

/**
 * @param {Record<string, string>} base
 * @param {Record<string, string>} defaults
 * @param {{ onlyIfEmpty?: boolean }} [opts]
 */
export function mergeEmployerDefaultsIntoForm(base, defaults, opts = {}) {
    const onlyIfEmpty = opts.onlyIfEmpty === true;
    const out = { ...base };
    for (const [k, v] of Object.entries(defaults)) {
        if (v === undefined || v === null || String(v).trim() === "") continue;
        if (onlyIfEmpty) {
            const cur = out[k];
            if (cur !== undefined && cur !== null && String(cur).trim() !== "") continue;
        }
        out[k] = v;
    }
    return out;
}

export function parseFieldOptions(raw) {
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const x of raw) {
        if (typeof x === "string") out.push(x);
        else if (x && typeof x === "object" && "value" in x) {
            const v = x.value;
            if (typeof v === "string") out.push(v);
        }
    }
    return out;
}

/** @param {unknown} raw */
export function parseFormColumns(raw) {
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const o = item;
        const field_key = typeof o.field_key === "string" ? o.field_key : "";
        if (!field_key) continue;
        const field_label = typeof o.field_label === "string" ? o.field_label : field_key;
        const field_type = typeof o.field_type === "string" ? o.field_type : "text";
        const is_required = o.is_required === true;
        out.push({
            field_key,
            field_label,
            field_type,
            is_required,
            field_options: o.field_options,
        });
    }
    return out;
}

/** @param {unknown} raw */
export function parseDrafts(raw) {
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const o = item;
        const id = typeof o.id === "string" ? o.id : "";
        if (!id) continue;
        const pd = o.participant_data;
        const participant_data =
            pd && typeof pd === "object" && !Array.isArray(pd) ? pd : {};
        out.push({ id, participant_data });
    }
    return out;
}

export function emptyDefaults(columns) {
    return Object.fromEntries(columns.map((c) => [c.field_key, ""]));
}

export function toStringValues(data) {
    const o = {};
    for (const [k, v] of Object.entries(data)) {
        if (v === null || v === undefined) o[k] = "";
        else if (typeof v === "object") o[k] = JSON.stringify(v);
        else o[k] = String(v);
    }
    return o;
}

/** Read flat or dotted keys from participant form state (same as corporate enrol pages). */
export function getNestedFormValue(data, fieldKey) {
    if (!data || !fieldKey) return "";
    if (data[fieldKey] !== undefined && data[fieldKey] !== null && data[fieldKey] !== "") {
        return data[fieldKey];
    }
    if (fieldKey.includes(".")) {
        const keys = fieldKey.split(".");
        let current = data;
        for (const k of keys) {
            if (current && typeof current === "object" && k in current) {
                current = current[k];
            } else {
                return "";
            }
        }
        return current ?? "";
    }
    return data[fieldKey] ?? "";
}

/** Participant draft payload for corporate RPCs, including signature companion keys. */
export function buildCorporateParticipantPayload(templateFieldRecords, formData) {
    const out = {};
    for (const r of templateFieldRecords) {
        if (!shouldShowOnCorporateEnrollForm(r)) continue;
        const v = getNestedFormValue(formData, r.field_key);
        if (v === null || v === undefined) out[r.field_key] = "";
        else if (typeof v === "object") out[r.field_key] = JSON.stringify(v);
        else out[r.field_key] = String(v);
    }
    for (const k of collectSignatureCompanionFieldKeys(templateFieldRecords)) {
        const v = getNestedFormValue(formData, k);
        if (v === null || v === undefined) out[k] = "";
        else if (typeof v === "object") out[k] = JSON.stringify(v);
        else out[k] = String(v);
    }
    return out;
}

export function pickParticipantPayload(columns, data) {
    const o = {};
    for (const c of columns) {
        o[c.field_key] = data[c.field_key] ?? "";
    }
    return o;
}

export function scrubDraftPayload(payload) {
    const out = { ...payload };
    for (const k of Object.keys(out)) {
        if (out[k] === "__none__") out[k] = "";
    }
    return out;
}

/** @param {FormColumn[]} columns */
export function validateDraftPayload(columns, data) {
    const errors = {};
    for (const c of columns) {
        const v = data[c.field_key] ?? "";
        if (c.field_type === "boolean" && c.is_required) {
            if (v !== "true" && v !== "false") {
                errors[c.field_key] = `${c.field_label} is required`;
            }
        } else if (c.is_required) {
            if (!String(v).trim() || v === "__none__") {
                errors[c.field_key] = `${c.field_label} is required`;
            }
        }
    }
    return { ok: Object.keys(errors).length === 0, errors };
}

export function csvEscapeCell(value) {
    const s = value == null ? "" : String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

export function matrixToCsvString(headers, rows) {
    const lines = [headers.map(csvEscapeCell).join(",")];
    for (const row of rows) {
        lines.push(row.map(csvEscapeCell).join(","));
    }
    return lines.join("\r\n");
}

export function draftsToCsv(columns, drafts) {
    const headers = columns.map((c) => c.field_label);
    const rows = drafts.map((d) =>
        columns.map((c) => {
            const v = d.participant_data[c.field_key];
            if (v === null || v === undefined) return "";
            if (typeof v === "object") return JSON.stringify(v);
            return String(v);
        }),
    );
    return matrixToCsvString(headers, rows);
}

export function templateCsv(columns) {
    return matrixToCsvString(
        columns.map((c) => c.field_label),
        [],
    );
}

/** Strip BOM and split CSV into rows with RFC 4180–style quoted fields. */
export function parseCsvText(text) {
    const s = text.replace(/^\uFEFF/, "");
    const rows = [];
    let row = [];
    let field = "";
    let i = 0;
    let inQuotes = false;

    while (i < s.length) {
        const c = s[i];
        if (inQuotes) {
            if (c === '"') {
                if (s[i + 1] === '"') {
                    field += '"';
                    i += 2;
                    continue;
                }
                inQuotes = false;
                i += 1;
                continue;
            }
            field += c;
            i += 1;
            continue;
        }
        if (c === '"') {
            inQuotes = true;
            i += 1;
            continue;
        }
        if (c === ",") {
            row.push(field);
            field = "";
            i += 1;
            continue;
        }
        if (c === "\r") {
            i += 1;
            continue;
        }
        if (c === "\n") {
            row.push(field);
            rows.push(row);
            row = [];
            field = "";
            i += 1;
            continue;
        }
        field += c;
        i += 1;
    }
    row.push(field);
    rows.push(row);
    return rows;
}

function normalizeBooleanCell(raw) {
    const t = raw.trim().toLowerCase();
    if (t === "") return "";
    if (["true", "yes", "y", "1"].includes(t)) return "true";
    if (["false", "no", "n", "0"].includes(t)) return "false";
    return raw.trim();
}

function rowStringsToRecord(columns, headerIndex, cells) {
    const o = {};
    for (const col of columns) {
        const idx = headerIndex.get(col.field_label);
        const raw = idx !== undefined && idx < cells.length ? cells[idx] ?? "" : "";
        const trimmed = raw.trim();
        if (col.field_type === "boolean") {
            o[col.field_key] = normalizeBooleanCell(trimmed);
        } else {
            o[col.field_key] = trimmed;
        }
    }
    return o;
}

export function buildCsvImportPreview(columns, text) {
    const matrix = parseCsvText(text);
    const headerErrors = [];

    if (matrix.length === 0 || (matrix.length === 1 && matrix[0].every((c) => c.trim() === ""))) {
        return { headerErrors: ["The file is empty."], rows: [] };
    }

    const headerCells = matrix[0].map((h) => h.trim());
    const headerIndex = new Map();
    for (let i = 0; i < headerCells.length; i += 1) {
        const label = headerCells[i];
        if (!label) continue;
        if (headerIndex.has(label)) {
            headerErrors.push(`Duplicate header column: "${label}"`);
        } else {
            headerIndex.set(label, i);
        }
    }

    for (const col of columns) {
        if (!headerIndex.has(col.field_label)) {
            headerErrors.push(`Missing column "${col.field_label}" (use the blank template headers).`);
        }
    }

    const rows = [];

    for (let r = 1; r < matrix.length; r += 1) {
        const cells = matrix[r];
        const allEmpty = cells.every((c) => c.trim() === "");
        if (allEmpty) continue;

        const data = rowStringsToRecord(columns, headerIndex, cells);
        const scrubbed = scrubDraftPayload(data);
        const { ok, errors } = validateDraftPayload(columns, scrubbed);
        const issues = Object.values(errors);

        rows.push({
            rowNumber: r + 1,
            data: pickParticipantPayload(columns, ok ? scrubbed : scrubbed),
            issues,
            ok,
        });
    }

    return { headerErrors, rows };
}

export function friendlyRpcError(error, fallback) {
    if (error && typeof error.message === "string" && error.message) return error.message;
    if (typeof error === "string") return error;
    return fallback;
}
